import { PaymentSettings } from '@/components/settings/organizations/Payments/PaymentSettings';
import usePermissions from '@/hooks/usePermissions';
import { OrganizationNode } from '@/types/Organizations';
import { Box, SelectInputNative } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState } from 'react';
import { NavLink, Route, Routes, useMatch, useNavigate, useParams } from 'react-router-dom';

import { ProgramType, ProgramTypeLabels, UserPermissions } from 'types';

import { ClawbacksSetting } from '../Clawback/ClawbacksSetting';
import { HoldbackSettings } from '../holdback/HoldbackSettings';
import { SafeHarborSettings } from '../safeHarbor/SafeHarborSettings';

import { OtherSettings } from './OtherSettings';

interface PaymentsProps {
  organization: OrganizationNode;
  isLoading?: boolean;
}

export const Payments = ({ organization }: PaymentsProps) => {
  const { alias, paymentsType } = useParams();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const { safeHarborOrgTransactions = false } = useFlags();
  const [programType, setProgramType] = useState<string>(organization?.programTypes?.[0] as string);
  const otherSettingsMatch = useMatch('/settings/organizations/:alias/payments/settings/other');

  useEffect(() => {
    if (!paymentsType) {
      navigate('settings');
    }
  }, []);

  const showClawbackSettingsSection = permissions?.some((permission) =>
    [UserPermissions.lightReachAccountingSettings, UserPermissions.orgAccountingAdmin].includes(permission),
  );

  const showHoldbackSettingsSection = permissions?.some((permission) =>
    [UserPermissions.lightReachAccountingSettings, UserPermissions.orgAccountingAdmin].includes(permission),
  );

  const showSafeHarborSettingsSection =
    safeHarborOrgTransactions &&
    permissions?.some((permission) =>
      [UserPermissions.lightReachAccountingSettings, UserPermissions.orgAccountingAdmin].includes(permission),
    );

  const programTypesOptions = useMemo(() => {
    return (
      organization?.programTypes?.map((pt) => ({
        label: ProgramTypeLabels[pt],
        value: pt,
      })) || []
    );
  }, [organization]);

  return (
    <Box>
      <Box direction="row" childGap="3xl">
        <Box>
          <NavLink
            to={`/settings/organizations/${alias}/payments/settings`}
            style={{ textDecoration: 'none', fontWeight: '500' }}
            key="paymentPlanSettings"
            end
          >
            {({ isActive }) => (
              <Box
                as="span"
                padding="md"
                className={isActive ? 'tab-active' : ''}
                hover={{ color: 'primary' }}
                style={{ transitionDuration: '0s', borderBottom: 0 }}
              >
                Payment Plan Settings
              </Box>
            )}
          </NavLink>
          {showClawbackSettingsSection && (
            <NavLink
              to={`/settings/organizations/${alias}/payments/settings/clawback-settings`}
              style={{ textDecoration: 'none', fontWeight: '500' }}
              key="clawbackSettings"
              end
            >
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s', borderBottom: 0 }}
                >
                  Clawback Settings
                </Box>
              )}
            </NavLink>
          )}
          {showHoldbackSettingsSection && (
            <NavLink
              to={`/settings/organizations/${alias}/payments/settings/holdback-settings`}
              style={{ textDecoration: 'none', fontWeight: '500' }}
              key="holdbackSettings"
              end
            >
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s', borderBottom: 0 }}
                >
                  Holdback Settings
                </Box>
              )}
            </NavLink>
          )}
          {showSafeHarborSettingsSection && (
            <NavLink
              to={`/settings/organizations/${alias}/payments/settings/safe-harbor-settings`}
              style={{ textDecoration: 'none', fontWeight: '500' }}
              key="safeHarborSettings"
              end
            >
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s', borderBottom: 0 }}
                >
                  Safe Harbor Settings
                </Box>
              )}
            </NavLink>
          )}
          <NavLink
            to={`/settings/organizations/${alias}/payments/settings/other`}
            style={{ textDecoration: 'none', fontWeight: '500' }}
            key="otherSettings"
            end
          >
            {({ isActive }) => (
              <Box
                as="span"
                padding="md"
                className={isActive ? 'tab-active' : ''}
                hover={{ color: 'primary' }}
                style={{ borderBottom: 0 }}
              >
                Other Settings
              </Box>
            )}
          </NavLink>
        </Box>
        <Box flex="auto" gap="md">
          {organization?.programTypes?.length && organization?.programTypes?.length > 1 && !otherSettingsMatch && (
            <Box maxWidth="2xl">
              <SelectInputNative
                id="required"
                label="Program Type"
                value={programType}
                onChange={(event) => setProgramType(event.target.value)}
                options={programTypesOptions}
                menuPortalTarget={document.body}
              />
            </Box>
          )}
          <Routes>
            <Route
              path="/"
              element={<PaymentSettings organization={organization} programType={programType as ProgramType} />}
            />
            {showClawbackSettingsSection && (
              <Route
                path="/clawback-settings"
                element={<ClawbacksSetting organization={organization} programType={programType as ProgramType} />}
              />
            )}
            {showHoldbackSettingsSection && (
              <Route
                path="/holdback-settings"
                element={<HoldbackSettings organization={organization} programType={programType as ProgramType} />}
              />
            )}
            {showSafeHarborSettingsSection && (
              <Route
                path="/safe-harbor-settings"
                element={<SafeHarborSettings organization={organization} programType={programType as ProgramType} />}
              />
            )}
            <Route path="/other" element={<OtherSettings organization={organization} />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};
